import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@parrot-software/design-system';

import { isAxiosError, hasApiErrorResponse, extractErrorInfo, getTranslationKey } from '@personnel/application/errors';
import { shouldHandleInForm, ROLE_FORM_FIELD_MAPPINGS } from '@personnel/domain/errors/formFieldMappings';

/**
 * Toast duration for all errors (4xx and 5xx)
 */
const ERROR_TOAST_DURATION = 6000;

/**
 * Action context for error messages
 */
export type ErrorActionContext = 'create' | 'update' | 'delete';

/**
 * Options for error handler
 */
export interface HandleErrorOptions {
  /** Action context to add to error message (e.g., "al crear el rol") */
  actionContext?: ErrorActionContext;
  /** Custom fallback message if no translation exists */
  fallbackMessage?: string;
}

/**
 * Hook for handling API errors and displaying toast notifications
 *
 * All errors are displayed as type "error" with 6000ms duration.
 *
 * Translation strategy:
 * 1. Extract error code (e.g., "roles.PermissionsNotFound")
 * 2. Map to correct i18n namespace (roles.json, employees.json, personnel.json)
 * 3. If translation exists → show translated message with context
 * 4. If not → show clean error code
 *
 * Error message format (when actionContext is provided):
 * "Ocurrió un error: [mensaje] al [crear|actualizar|borrar] el rol. Intenta nuevamente."
 *
 * @returns Object with handleError function
 *
 * @example
 * const { handleError } = useApiErrorHandler();
 *
 * try {
 *   await createRole(data);
 * } catch (error) {
 *   handleError(error, { actionContext: 'create' });
 * }
 */
export const useApiErrorHandler = () => {
  const { t: tPersonnel, i18n } = useTranslation('personnel');
  const { t: tRoles } = useTranslation('roles');
  const { t: tEmployees } = useTranslation('personnel-employees');

  /**
   * Handles API errors and displays appropriate toast notifications
   *
   * @param error - Error from API call (AxiosError or unknown)
   * @param options - Error handling options
   */
  const handleError = useCallback(
    (error: unknown, options?: HandleErrorOptions) => {
      const { actionContext, fallbackMessage } = options || {};
      // Case 1: Axios error with structured API response
      if (isAxiosError(error) && hasApiErrorResponse(error)) {
        const statusCode = error.response?.status || 500;
        const apiErrorResponse = error.response?.data;

        // Safety check: ensure response data exists
        if (!apiErrorResponse) {
          toast.error(tPersonnel('errors.common.UnexpectedError'), {
            duration: ERROR_TOAST_DURATION,
          });
          return;
        }

        // Extract error information
        const errorInfo = extractErrorInfo(apiErrorResponse, statusCode);

        // Check if this error should be handled by the form instead of showing a toast
        if (shouldHandleInForm(errorInfo.code, ROLE_FORM_FIELD_MAPPINGS)) {
          // Skip toast - error will be handled by form validation
          // The mutation's onError will catch this and set it on the form field
          return;
        }

        // Get correct namespace and translation key
        const { namespace, key } = getTranslationKey(errorInfo.code);

        // Select correct translation function
        const t =
          namespace === 'roles'
            ? tRoles
            : namespace === 'personnel-employees' || namespace === 'employees'
            ? tEmployees
            : tPersonnel;

        // Build full translation key
        const fullKey = `${namespace}:${key}`;

        // Check if translation exists
        let displayMessage: string;

        if (i18n.exists(fullKey)) {
          // Translation exists - use it
          const errorMessage = t(key, errorInfo.interpolationData);

          // Build complete message with context if provided
          if (actionContext && namespace === 'roles') {
            const prefix = tRoles('errors.prefix');
            const context = tRoles(`errors.context.${actionContext}`);
            const suffix = tRoles('errors.suffix');

            displayMessage = `${prefix} ${errorMessage} ${context} ${suffix}`;
          } else {
            displayMessage = errorMessage;
          }
        } else {
          // No translation - show clean error code
          displayMessage = errorInfo.code;
        }

        // Show toast with custom width for controlled errors (with context)
        toast.error(displayMessage, {
          duration: ERROR_TOAST_DURATION,
          description: apiErrorResponse.validation || undefined,
          className: actionContext ? 'w-[643px]!' : undefined,
        });

        return;
      }

      // Case 2: Axios error without structured response
      if (isAxiosError(error)) {
        let message: string;

        // Sub-case 2.1: Network connectivity issues (no response from server)
        if (error.code === 'ERR_NETWORK') {
          message = tPersonnel('errors.common.NetworkError');
        }
        // Sub-case 2.2: Server responded but with non-structured error (5xx)
        else if (error.response?.status && error.response.status >= 500) {
          message = tPersonnel('errors.common.ServerError');
        }
        // Sub-case 2.3: Other Axios errors (canceled, timeout, config issues)
        else {
          message = tPersonnel('errors.common.UnexpectedError');
        }

        toast.error(message, {
          duration: ERROR_TOAST_DURATION,
        });

        return;
      }

      // Case 3: Unknown error (fallback)
      const message = fallbackMessage || tPersonnel('errors.common.UnexpectedError');
      toast.error(message, {
        duration: ERROR_TOAST_DURATION,
      });
    },
    [tPersonnel, tRoles, tEmployees, i18n],
  );

  return { handleError };
};
